// This was generated during the migration to @kiwicom/eslint-config
// PLEASE REMOVE THE GLOBAL ESLINT DISABLE BELOW
// AND FIX ERRORS ACCORDINGLY.
/* eslint-disable new-cap, babel/new-cap, func-names, default-param-last */
import { List, Map, Record } from "immutable"
import type { Dispatch } from "redux"
import * as PickerFormComposer from "@kiwicom/frontend-app/scripts/composers/PickerFormComposer"
import type { FormFieldType } from "@kiwicom/frontend-common/consts/form"
import { FORM_FIELDS, NEXT_FIELD } from "@kiwicom/frontend-common/consts/form"
import SearchDate from "@kiwicom/frontend-common/records/SearchDate"
import SearchPlace from "@kiwicom/frontend-common/records/SearchPlace"
import * as PickerPricesComposer from "@kiwicom/frontend-common/services/pickers/pickerPricesComposer"
import type Journey from "@kiwicom/frontend-common/records/Journey"

import type {
  Action,
  ChangeFlightsStateInst,
  ChangeFlightsStateType,
  CheckFlightAction,
  ClearAlternativeAction,
  ClearOriginalAction,
  ClearStateAction,
  FieldsRecordType,
  FormRecordType,
  InitFromApiAction,
  OpenCloseFormAction,
  OpenContactFormAction,
  SelectAlternativeAction,
  SelectOriginalAction,
  SetActiveFieldAction,
  SetActiveModeAction,
  SetFormAction,
  SetNextFieldAction,
  SetValueAction,
  SubmitRecordType,
} from "./types"
import {
  CHECK_FLIGHT,
  CHECK_FLIGHT_FAILURE,
  CHECK_FLIGHT_REQUEST,
  CHECK_FLIGHT_SUCCESS,
  CLEAR_ALTERNATIVE,
  CLEAR_ORIGINAL,
  CLEAR_STATE,
  CONFIRM_FAILURE,
  CONFIRM_REQUEST,
  CONFIRM_SUCCESS,
  CONFIRM_SUCCESS_FREE_EXTRA,
  FORM_ACTIVE_FIELD,
  FORM_ACTIVE_MODE,
  FORM_CHANGE_FORM,
  FORM_CHANGE_VALUE,
  FORM_NEXT_FIELD,
  FORM_SUBMIT,
  FORM_SUBMIT_FAILURE,
  FORM_SUBMIT_REQUEST,
  FORM_SUBMIT_SUCCESS,
  INITIATOR_API,
  SELECT_ALTERNATIVE,
  SELECT_ORIGINAL,
  SET_CONTACT_FORM_OPEN,
  SET_FORM_OPEN,
} from "./consts"
import {
  CABIN_TYPE_MIXED_ALLOWED_DEFAULT,
  MAX_TRAVEL_DURATION_DEFAULT,
  TRANSPORT_TYPES_DEFAULT,
} from "../../scenes/BookingJourneyChange/components/ChangeAlternatives/Filters/consts"
import { searchAlternatives } from "../../scenes/BookingJourneyChange/services/AlternativeFlightsDuck"
import { initialCabinTypeSelector } from "./selector"
import type { TransportType } from "../../scenes/BookingJourneyChange/components/ChangeAlternatives/Filters/TransportTypeFilter"

// Refactor to one "phase" with enum values
const SubmitRecord: SubmitRecordType = Record({
  success: false,
  loading: false,
  error: null,
})

const FieldsRecord: FieldsRecordType = Record({
  origin: new SearchPlace(),
  destination: new SearchPlace(),
  outboundDate: new SearchDate(),
  inboundDate: new SearchDate(),
  message: null,
  sort: "quality",
  cabinType: undefined,
  cabinTypeMixedAllowed: false,
  transportTypes: ["aircraft", "bus", "train"],
  maxTravelDuration: 60,
})

const FormRecord: FormRecordType = Record({
  fields: new FieldsRecord(),
  activeModes: new PickerFormComposer.ActiveModesRecord(), // Active modes for all active fields
  activeField: null,
  prices: new PickerPricesComposer.PricesRecord(),
  submit: new SubmitRecord(),
})

const ChangeFlightsState: ChangeFlightsStateType = Record({
  selected: null, // selected journey/part to change
  alternatives: null, // available alternatives
  alternative: null, // desired one from alternatives
  initiatorApi: false, // @strajk rename to initialisedFromApi
  formOpen: true,
  contactFormOpen: false,
  checkFlight: new SubmitRecord(),
  confirmation: new SubmitRecord(),
  form: new FormRecord(),
})

// @ts-expect-error: Flow to TS migration.
function fillChangeFlightsState(map: Map): ChangeFlightsStateInst {
  return new ChangeFlightsState({
    ...map.toJS(),
    checkFlight: new SubmitRecord(map.get("checkFlight")),
    confirmation: new SubmitRecord(map.get("confirmation")),
    form: new FormRecord({
      ...map.get("form").toJS(),
      fields: new FieldsRecord({
        ...map.getIn(["form", "fields"]).toJS(),
        origin: new SearchPlace(map.getIn(["form", "fields", "origin"])),
        destination: new SearchPlace(map.getIn(["form", "fields", "destination"])),
        outboundDate: new SearchDate(map.getIn(["form", "fields", "outboundDate"])),
        inboundDate: new SearchDate(map.getIn(["form", "fields", "inboundDate"])),
      }),
      submit: new SubmitRecord(map.getIn(["form", "submit"])),
      prices: new PickerPricesComposer.PricesRecord(),
      activeModes: new PickerFormComposer.ActiveModesRecord(),
    }),
  })
}

export default function (
  // Fix this the next time the file is edited. (Flow to TS)
  // @ts-expect-error: Flow to TS migration.
  // eslint-disable-next-line no-undef
  state: ChangeFlightsState | null | undefined = new ChangeFlightsState(),
  action: Action,
) {
  if (!(state instanceof ChangeFlightsState)) return fillChangeFlightsState(state)

  switch (action.type) {
    case SELECT_ORIGINAL:
      return state.set("selected", action.journey)

    case CLEAR_ORIGINAL:
      return state.set("selected", null)

    case SELECT_ALTERNATIVE:
      return state.set("alternative", action.journey)

    case CLEAR_ALTERNATIVE:
      return state.set("alternative", null)

    case INITIATOR_API:
      return state
        .set("initiatorApi", true)
        .set("formOpen", false)
        .set("selected", action.selected)
        .set("alternatives", List(action.alternatives))

    case CLEAR_STATE:
      return new ChangeFlightsState()

    case SET_FORM_OPEN:
      return state.set("formOpen", action.payload)

    case SET_CONTACT_FORM_OPEN:
      return state.set("contactFormOpen", action.payload)

    // check flight
    // ===
    case CHECK_FLIGHT_REQUEST:
      return state
        .setIn(["checkFlight", "success"], false)
        .setIn(["checkFlight", "loading"], true)
        .setIn(["checkFlight", "error"], null)

    case CHECK_FLIGHT_SUCCESS:
      return state
        .setIn(["checkFlight", "success"], true)
        .setIn(["checkFlight", "loading"], false)
        .setIn(["checkFlight", "error"], null)

    case CHECK_FLIGHT_FAILURE:
      return state
        .set("alternative", null)
        .setIn(["checkFlight", "success"], false)
        .setIn(["checkFlight", "loading"], false)
        .setIn(["checkFlight", "error"], action.error)

    // confirmation
    // ===
    case CONFIRM_REQUEST:
      return state
        .setIn(["confirmation", "success"], false)
        .setIn(["confirmation", "loading"], true)
        .setIn(["confirmation", "error"], null)

    case CONFIRM_SUCCESS:
      return state
        .setIn(["confirmation", "success"], !action.isFreeBasketExtra)
        .setIn(["confirmation", "loading"], false)
        .setIn(["confirmation", "error"], null)

    case CONFIRM_FAILURE:
      return state
        .setIn(["confirmation", "success"], false)
        .setIn(["confirmation", "loading"], false)
        .setIn(["confirmation", "error"], action.error)

    case CONFIRM_SUCCESS_FREE_EXTRA:
      return state.setIn(["confirmation", "success"], true)

    // form
    // ===
    case FORM_ACTIVE_MODE:
      return state.setIn(["form", "activeModes", action.field], action.mode)

    case FORM_ACTIVE_FIELD:
      return state.setIn(["form", "activeField"], action.field)

    case FORM_NEXT_FIELD: {
      // @ts-expect-error: Flow to TS migration.
      return state.setIn(["form", "activeField"], NEXT_FIELD[state.form.activeField])
    }

    case FORM_CHANGE_FORM:
      return state.setIn(["form", "fields"], new FieldsRecord(action.form))

    case FORM_CHANGE_VALUE:
      return state.setIn(
        ["form", "fields"],
        PickerFormComposer.handleFormChange(state.form.fields, action.value, action.field),
      )

    // submit
    // ---
    case FORM_SUBMIT_REQUEST:
      return state
        .setIn(["form", "submit", "success"], false)
        .setIn(["form", "submit", "loading"], true)
        .setIn(["form", "submit", "error"], null)

    case FORM_SUBMIT_SUCCESS:
      return state
        .setIn(["form", "submit", "success"], true)
        .setIn(["form", "submit", "loading"], false)
        .setIn(["form", "submit", "error"], null)

    case FORM_SUBMIT_FAILURE:
      return state
        .setIn(["form", "submit", "success"], false)
        .setIn(["form", "submit", "loading"], false)
        .setIn(["form", "submit", "error"], action.error)

    default:
      return state
  }
}

export function selectOriginal(journey: Journey): SelectOriginalAction {
  return {
    type: SELECT_ORIGINAL,
    journey,
    // @ts-expect-error: Flow to TS migration.
    _track: { journey },
  }
}

export function clearOriginal(): ClearOriginalAction {
  return {
    type: CLEAR_ORIGINAL,
    // @ts-expect-error: Flow to TS migration.
    _track: {},
  }
}

export function selectAlternative(journey: Journey): SelectAlternativeAction {
  return {
    type: SELECT_ALTERNATIVE,
    journey,
    // @ts-expect-error: Flow to TS migration.
    _track: { journey },
  }
}

export function clearAlternative(): ClearAlternativeAction {
  return {
    type: CLEAR_ALTERNATIVE,
    // @ts-expect-error: Flow to TS migration.
    _track: {},
  }
}

export function initFromApi(selected: Journey, alternatives: Journey[]): InitFromApiAction {
  return {
    type: INITIATOR_API,
    selected,
    alternatives,
    // @ts-expect-error: Flow to TS migration.
    _track: { selected, alternatives },
  }
}

export function clearState(): ClearStateAction {
  return {
    type: CLEAR_STATE,
  }
}

export function openForm(): OpenCloseFormAction {
  return {
    type: SET_FORM_OPEN,
    payload: true,
  }
}

export function closeForm(): OpenCloseFormAction {
  return {
    type: SET_FORM_OPEN,
    payload: false,
  }
}

export function openContactForm(): OpenContactFormAction {
  return {
    type: SET_CONTACT_FORM_OPEN,
    payload: true,
  }
}

// Fix this the next time the file is edited. (Flow to TS)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function checkFlight(promise: Promise<any>): CheckFlightAction {
  return {
    type: CHECK_FLIGHT,
    promise,
    // @ts-expect-error: Flow to TS migration.
    _track: {},
  }
}

// form
// ===
export function setActiveMode(
  field: FormFieldType,
  // @ts-expect-error: Flow to TS migration.
  mode: PickerFormComposer.ActiveModesRecord,
): SetActiveModeAction {
  return {
    type: FORM_ACTIVE_MODE,
    field,
    mode,
  }
}

export function setActiveField(field: FormFieldType): SetActiveFieldAction {
  return {
    type: FORM_ACTIVE_FIELD,
    field,
  }
}

export function setNextField(): SetNextFieldAction {
  return {
    type: FORM_NEXT_FIELD,
  }
}

export function setValue(
  field: FormFieldType,
  value: string | number | boolean | TransportType[],
): SetValueAction {
  return {
    type: FORM_CHANGE_VALUE,
    field,
    value,
  }
}

function updateFilterFormFields(dispatch: Dispatch<SetValueAction>, defaultCabinType: string) {
  // @ts-expect-error: Flow to TS migration.
  dispatch(setValue(FORM_FIELDS.TRANSPORT_TYPES, TRANSPORT_TYPES_DEFAULT))
  dispatch(setValue(FORM_FIELDS.CABIN_TYPE_MIXED_ALLOWED, CABIN_TYPE_MIXED_ALLOWED_DEFAULT))
  dispatch(setValue(FORM_FIELDS.CABIN_TYPE, defaultCabinType))
  dispatch(setValue(FORM_FIELDS.MAX_TRAVEL_DURATION, MAX_TRAVEL_DURATION_DEFAULT))
}

// Fix this the next time the file is edited. (Flow to TS)
// @ts-expect-error: Flow to TS migration.
// eslint-disable-next-line no-undef
export function setForm(form: FieldsRecord): SetFormAction {
  return {
    type: FORM_CHANGE_FORM,
    form,
  }
}

// Fix this the next time the file is edited. (Flow to TS)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function submitForm(promise: Promise<any>) {
  return {
    type: FORM_SUBMIT,
    promise,
    _track: {},
  }
}

// Fix this the next time the file is edited. (Flow to TS)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const resetFilters = () => async (dispatch: Dispatch<any>, getState: () => any) => {
  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/await-thenable
  await updateFilterFormFields(dispatch, initialCabinTypeSelector(getState()))
  return dispatch(searchAlternatives())
}
